import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Box,
  Typography,
  Alert,
  Divider,
  Paper,
  Button,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useCustomPageController } from './context/custom-page-controller';
import { useCustomPageState } from './context/custom-page-context';
import { RichTextContent } from './rich-text-content';
import { CustomPageSections } from './custom-page-sections';

export function CustomPageComponent({ legacyArticle = false }: { legacyArticle?: boolean }) {
  // Slug da página customizada ("/pagina/{slug}") ou, na rota antiga, o id do artigo legado
  const { slug } = useParams<{ slug: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadArticle, loadCustomPage } = useCustomPageController();
  const { detail, page, loading, error } = useCustomPageState();

  useEffect(() => {
    if (!slug) return;

    if (legacyArticle) {
      loadArticle(parseInt(slug));
    } else {
      loadCustomPage(slug);
    }
  }, [slug, legacyArticle]);

  useEffect(() => {
    if (page?.title) {
      document.title = page.title;
    }
  }, [page?.title]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const backButton = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: 4,
        mb: 3,
      }}
    >
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        size="large"
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 4,
          py: 1.5,
        }}
      >
        Voltar para Início
      </Button>
    </Box>
  );

  const renderMessage = (severity: 'error' | 'info', message: string) => (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="Página Customizada"
          breadcrumbs={[
            { label: 'Página Customizada' },
          ]}
        />
        <Alert severity={severity} sx={{ mt: 2 }}>
          {message}
        </Alert>
        {backButton}
      </Container>
    </PageContainer>
  );

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={1} />;
  }

  if (error) {
    return renderMessage('error', error);
  }

  if (page) {
    const lastUpdate = page.updatedAt || page.publishedAt;

    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title={page.title || 'Página Customizada'}
            breadcrumbs={[
              { label: page.title || 'Página Customizada' },
            ]}
          />

          {page.summary && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {page.summary}
            </Typography>
          )}

          {page.coverImageUrl && (
            <Box
              component="img"
              src={page.coverImageUrl}
              alt={page.title || ''}
              sx={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 2, display: 'block', mt: 2, mb: 3 }}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <CustomPageSections sections={page.sections} />
          </Box>

          {lastUpdate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, color: 'text.secondary' }}>
              <CalendarTodayIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                Atualizado em {formatDate(lastUpdate)}
              </Typography>
            </Box>
          )}

          {backButton}
        </Container>
      </PageContainer>
    );
  }

  if (!detail) {
    return renderMessage('info', 'A página solicitada não foi encontrada ou não está mais disponível.');
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={detail.title || 'Página Customizada'}
          breadcrumbs={[
            { label: detail.title || 'Página Customizada' },
          ]}
        />

        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 2,
            borderRadius: 2,
          }}
        >
          {/* Cabeçalho com data de publicação */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                Publicado em {formatDate(detail.createdDate)}
              </Typography>
            </Box>
            {detail.modifiedDate && (
              <>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Atualizado em {formatDate(detail.modifiedDate)}
                </Typography>
              </>
            )}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Conteúdo do artigo com normalização de HTML */}
          <RichTextContent html={detail.content} />

          {!detail.content && (
            <Alert severity="info">
              Este artigo não possui conteúdo disponível.
            </Alert>
          )}
        </Paper>

        {backButton}
      </Container>
    </PageContainer>
  );
}
